/* eslint-disable @typescript-eslint/no-empty-function */

import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cookie from 'react-cookies';

import {
  LEGACY_ROLES,
  PERMISSIONS,
} from 'components/account/AccountView/users/constants';
import {
  LegacyRoles,
  Permissions,
} from 'components/account/AccountView/users/types';
import { ViewOptions, VIEW_OPTIONS } from 'components/sales/constants';
import Constants from 'controllers/constants';
import { Requests } from 'controllers/network';
import { initializeAxiosClient } from 'controllers/network/apiClient';
import { logOutAccess, logOutRefresh } from 'controllers/utils';
import { User } from 'types';
import { createSafeContext } from 'utils/createSafeContext';
import { CSRF_TOKEN_NAME } from 'utils/envVars';

const { internalApiHost } = Constants;

const REFRESH_TOKEN_ENDPOINT = '/token/refresh';

interface AuthContext {
  accessToken: string | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  clearAccessToken: () => void;
  saveAccessToken: (token: string | null) => void;
  refreshAccessToken: () => Promise<string | null>;
  logOut: () => Promise<boolean>;
  getAccessTokenPayload: () => Record<string, any> | undefined;
  getShopId: () => number;
  getUserPermissions: () => Permissions[] | undefined;
  userHasPermission: (permission: Permissions) => boolean;
  getLegacyUserRoles: () => LegacyRoles[];
  userHasLegacyRole: (role: LegacyRoles) => boolean;
  getMaxViewLevelForUser: (user: Partial<User>) => ViewOptions;
}

export const [Provider, useAuth] = createSafeContext<AuthContext>(
  'AuthContext',
  'useAuth must be a descendant of AuthContextProvider',
);

const parseJwt = (token: string): Record<string, any> => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const payload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(
        (character) =>
          `%${`00${character.charCodeAt(0).toString(16)}`.slice(-2)}`,
      )
      .join(''),
  );

  return JSON.parse(payload);
};

const createRefreshTokenConfig = () => {
  return {
    headers: {
      'X-CSRF-TOKEN': cookie.load(CSRF_TOKEN_NAME),
    },
    withCredentials: true,
  };
};

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initial loading state while we check if the user is logged in
  const [isLoading, setIsLoading] = useState(true);

  const [accessToken, setAccessTokenState] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null,
  );

  const handleSetIsLoggedIn = useCallback((loggedIn: boolean) => {
    setIsLoggedIn(loggedIn);
  }, []);

  const saveAccessToken = useCallback((token: string | null) => {
    setAccessTokenState(token);
    handleSetIsLoggedIn(!!token);
  }, []);

  const clearAccessToken = useCallback(() => {
    saveAccessToken(null);
  }, []);

  const logOut = useCallback(async () => {
    await logOutAccess();

    clearAccessToken();

    await logOutRefresh();

    return true;
  }, [clearAccessToken]);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const {
        data: { access_token: token },
      } = await axios.post(
        `${internalApiHost}${REFRESH_TOKEN_ENDPOINT}`,
        {},
        createRefreshTokenConfig(),
      );

      if (token) {
        saveAccessToken(token);
        return token;
      }

      clearAccessToken();
      return null;
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error(error.response?.data?.msg || error.message);
      }

      clearAccessToken();
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, saveAccessToken, clearAccessToken]);

  // TODO: Should validate the token and type the return value here
  const getAccessTokenPayload = useCallback(() => {
    if (!accessToken) return;
    return parseJwt(accessToken);
  }, [accessToken]);

  const getShopId = useCallback(() => {
    const accessTokenPayload = getAccessTokenPayload();
    return accessTokenPayload?.shop_id as number;
  }, [getAccessTokenPayload]);

  const getUserPermissions = useCallback((): Permissions[] | undefined => {
    const accessTokenPayload = getAccessTokenPayload();
    return accessTokenPayload?.identity?.permissions;
  }, [getAccessTokenPayload]);

  const userHasPermission = useCallback(
    (permission: Permissions): boolean => {
      const permissions = getUserPermissions();
      return permissions ? permissions.includes(permission) : false;
    },
    [getUserPermissions],
  );

  const getLegacyUserRoles = useCallback((): LegacyRoles[] => {
    const accessTokenPayload = getAccessTokenPayload();
    return accessTokenPayload?.identity?.roles || [];
  }, [getAccessTokenPayload]);

  const userHasLegacyRole = useCallback(
    (role: LegacyRoles): boolean => {
      const roles = getLegacyUserRoles();
      return roles ? roles.includes(role) : false;
    },
    [getLegacyUserRoles],
  );

  const getMaxViewLevelForUser = useCallback(
    (user: Partial<User>) => {
      if (userHasLegacyRole(LEGACY_ROLES.AI_ALPHA_TESTER)) {
        return VIEW_OPTIONS.AI_DEBUGGER;
      }

      if (
        user.is_admin ||
        userHasPermission(PERMISSIONS.limit_sms_sales_agent_access)
      ) {
        return VIEW_OPTIONS.SALES_ASSOCIATE;
      }

      return VIEW_OPTIONS.MERCHANT;
    },
    [userHasLegacyRole, userHasPermission],
  );

  const authApi = useMemo(
    () => ({
      accessToken,
      isLoading,
      isLoggedIn,
      clearAccessToken,
      saveAccessToken,
      refreshAccessToken,
      logOut,
      getAccessTokenPayload,
      getShopId,
      getUserPermissions,
      userHasPermission,
      getLegacyUserRoles,
      userHasLegacyRole,
      getMaxViewLevelForUser,
    }),
    [
      accessToken,
      isLoading,
      isLoggedIn,
      clearAccessToken,
      saveAccessToken,
      refreshAccessToken,
      logOut,
      getAccessTokenPayload,
      getShopId,
      getUserPermissions,
      userHasPermission,
      getLegacyUserRoles,
      userHasLegacyRole,
      getMaxViewLevelForUser,
    ],
  );

  const initRefreshCycle = useCallback((accessToken: string) => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }

    const jwtPayload = parseJwt(accessToken);
    const expiresAt = jwtPayload?.exp;

    if (expiresAt) {
      const now = new Date();
      const timeUntilExpiration = expiresAt * 1000 - now.getTime();

      if (timeUntilExpiration > 0) {
        const interval = setInterval(() => {
          authApi.refreshAccessToken();
        }, timeUntilExpiration - 2000);

        setRefreshInterval(interval);
      }
    }
  }, []);

  // The utility functions defined in the Requests class and the axios interceptors need to plug into the React runtime in order
  // to access the auth context, so this is how we do it: Requests is a singleton, so when we have
  // this authApi object, we can just save it to the Requests instance, and then the axios interceptors are
  // recreated with the new authApi object.
  useEffect(() => {
    initializeAxiosClient(authApi.accessToken);
    Requests.setAuthApi(authApi);

    if (authApi.accessToken && !refreshInterval) {
      initRefreshCycle(authApi.accessToken);
    }
  }, [authApi.accessToken, refreshInterval, initRefreshCycle]);

  useEffect(() => {
    if (!accessToken) {
      handleSetIsLoggedIn(false);
    }
  }, [accessToken]);

  return <Provider value={authApi}>{children}</Provider>;
};

export const withAuth = (Component: React.ComponentType<any>) => {
  return (props: any) => {
    const authApi = useAuth();

    return <Component {...props} auth={authApi} />;
  };
};

export default useAuth;
